/* eslint-disable sonarjs/cognitive-complexity */
'use client'

import { Field } from 'formik'
import { Check } from 'lucide-react'
import * as React from 'react'
import { useContext } from 'react'
import { Image } from '@chakra-ui/react'

import { thickBoxShadow, defaultBorder } from '@/chakraTheme'
import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { AuthContext } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import vector5 from '../assets/vector_5.svg'

// Helper function to extract full name from email
const getFullNameFromEmail = (email) => {
    if (!email) return '';
    const firstPart = email?.split('@')[0];
    const nameParts = firstPart?.split('.');
    
    if (nameParts?.length >= 2) {
        const firstName = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const lastName = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
        return `${firstName} ${lastName}`;
    }
    
    // If there's only one part, just capitalize it
    return firstPart.charAt(0).toUpperCase() + firstPart.slice(1);
};

export function SearchableSelect({
    name,
    label,
    placeholder,
    width = 'w-[549px]',
    initialOption = null,
    options = [],
    onSelect,
    isDisabled = false
}) {
    const [open, setOpen] = React.useState(false)
    const [filteredOptions, setFilteredOptions] = React.useState(
        initialOption ? [initialOption, ...options] : options
    )
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
    const commandListRef = React.useRef(null)
    const optionRefs = React.useRef([])
    const buttonRef = React.useRef(null)

    // Update refs array when options change
    React.useEffect(() => {
        optionRefs.current = Array(filteredOptions.length).fill().map((_, i) => optionRefs.current[i] || React.createRef())
    }, [filteredOptions])

    React.useEffect(() => {
        setFilteredOptions(
            initialOption 
                ? [initialOption, ...options].filter(
                    (opt, index, self) => 
                        index === self.findIndex(t => t.value === opt.value)
                ) 
                : options
        )
    }, [initialOption, options])

    const handleSearch = React.useCallback((inputValue) => {
        const searchValue = inputValue?.toLowerCase()
        const filtered = options?.filter(option => 
            option?.value?.toLowerCase()?.includes(searchValue) ||
            option?.label?.toLowerCase()?.includes(searchValue)
        )
        setFilteredOptions(filtered)
        setHighlightedIndex(-1)
    }, [options])

    const scrollOptionIntoView = (index) => {
        if (index < 0 || !commandListRef.current || !optionRefs.current[index]?.current) return;
        
        const listElement = commandListRef.current;
        const optionElement = optionRefs.current[index].current;
        
        if (listElement && optionElement) {
            const listRect = listElement.getBoundingClientRect();
            const optionRect = optionElement.getBoundingClientRect();
            
            // Check if option is not fully visible
            if (optionRect.bottom > listRect.bottom) {
                // Need to scroll down
                listElement.scrollTop += (optionRect.bottom - listRect.bottom);
            } else if (optionRect.top < listRect.top) {
                // Need to scroll up
                listElement.scrollTop -= (listRect.top - optionRect.top);
            }
        }
    };

    const handleKeyDown = (event, form) => {
        if (!open) return

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                setHighlightedIndex(prev => {
                    const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
                    // Schedule scrolling after state update
                    setTimeout(() => scrollOptionIntoView(newIndex), 0);
                    return newIndex;
                });
                break;
            case 'ArrowUp':
                event.preventDefault()
                setHighlightedIndex(prev => {
                    const newIndex = prev > 0 ? prev - 1 : prev;
                    // Schedule scrolling after state update
                    setTimeout(() => scrollOptionIntoView(newIndex), 0);
                    return newIndex;
                });
                break;
            case 'Enter':
                event.preventDefault()
                if (highlightedIndex >= 0) {
                    const selectedOption = filteredOptions[highlightedIndex]
                    form.setFieldValue(name, selectedOption.value)
                    form.setFieldTouched(name, true, false)
                    if (onSelect) onSelect(selectedOption.value)
                    setOpen(false)
                    setHighlightedIndex(-1)
                    
                    // Remove focus from the button to prevent double border
                    if (buttonRef?.current) {
                        buttonRef?.current?.blur()
                    }
                }
                break;
            case 'Escape':
                setOpen(false)
                setHighlightedIndex(-1)
                break;
            default:
                break;
        }
    }

    const handleSelect = React.useCallback((currentValue, form) => {
        form.setFieldValue(name, currentValue)
        form.setFieldTouched(name, true, false)
        if (onSelect) onSelect(currentValue)
        setOpen(false)
        setHighlightedIndex(-1)
        
        // Remove focus from the button to prevent double border
        if (buttonRef.current) {
            buttonRef.current.blur()
        }
    }, [name, onSelect])

    React.useEffect(() => {
        if (!open) {
            setHighlightedIndex(-1)
        }
    }, [open])

    return (
        <Field name={name}>
            {({ field, form, meta }) => (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '8px',
                    borderRadius: 0,
                    opacity: isDisabled ? 0.5 : 1,
                    pointerEvents: isDisabled ? 'none' : 'auto'
                }}>
                    {label && (
                        <label
                            htmlFor={name}
                            style={{ color: '#191919', fontSize: '14px' }}
                        >
                            {label}
                            <span style={{ color: '#CB3434' }}>*</span>
                        </label>
                    )}

                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                ref={buttonRef}
                                variant="ghost"
                                role="combobox"
                                aria-expanded={open}
                                className={cn('justify-between border focus:ring-0 focus:ring-offset-0', width)}
                                style={{
                                    padding: '16px 24px',
                                    height: '56px',
                                    width: '549px',
                                    flexShrink: 0,
                                    borderRadius: 0,
                                    border: defaultBorder,
                                    color: field.value ? '#191919' : '#808080',
                                    cursor: 'pointer',
                                    outline: 'none',
                                }}
                            >
                                {field.value
                                    ? name === 'facilitator' 
                                      ? (() => {
                                          const option = filteredOptions.find(opt => opt?.value === field.value);
                                          if (option) {
                                            // Check if option.label already contains the email
                                            if (option.label.includes(field.value)) {
                                              return option.label;
                                            }
                                            return `${option.label} (${field.value})`;
                                          }
                                          return `${getFullNameFromEmail(field.value)} (${field.value})`;
                                        })()
                                      : field.value
                                    : placeholder}
                                <Image 
                                    src={vector5} 
                                    alt="dropdown arrow"
                                    style={{
                                        width: '12px',
                                        height: '6px',
                                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease'
                                    }}
                                />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent
                            className={cn('p-0', width)}
                            style={{ 
                                zIndex: 9999,
                                borderRadius: 0,
                                overflow: 'hidden'
                            }}
                        >
                            <Command
                                style={{
                                    boxShadow: thickBoxShadow,
                                    border: '3px solid #333333',
                                    borderRadius: 0
                                }}
                            >
                                <CommandInput
                                    placeholder={placeholder}
                                    onValueChange={handleSearch}
                                    onKeyDown={event => handleKeyDown(event, form)}
                                    className="font-roboto text-[14px] text-[#191919]"
                                    style={{ borderRadius: 0 }}
                                />
                                <CommandList
                                    ref={commandListRef}
                                    className="max-h-[276px]"
                                    style={{
                                        display: open ? 'flex' : 'none',
                                        minWidth: 0,
                                        width: 'calc(100% - 5px)',
                                        borderRadius: 0,
                                        padding: 0,
                                        flexDirection: 'column',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <CommandEmpty className="p-4 text-center font-roboto text-[14px] text-[#808080]">
                                        No matches found
                                    </CommandEmpty>
                                    <CommandGroup>
                                        {filteredOptions.map((option, index) => (
                                            <CommandItem
                                                ref={optionRefs.current[index]}
                                                key={option.value}
                                                value={option.value}
                                                onSelect={currentValue => handleSelect(currentValue, form)}
                                                className={cn(
                                                    'font-roboto aria-selected:bg-[#F5F5F5] cursor-pointer flex justify-between items-center py-2 px-4 hover:bg-[#f5f5f5]',
                                                    highlightedIndex === index && 'bg-[#F5F5F5]'
                                                )}
                                                style={{
                                                    borderBottom: '1px solid #e3e3e3'
                                                }}
                                            >
                                                <div className="flex flex-col gap-1">
                                                    <span style={{
                                                        color: '#191919',
                                                        fontFamily: 'Roboto',
                                                        fontSize: '16px',
                                                        fontStyle: 'normal',
                                                        fontWeight: '400',
                                                        lineHeight: '150%'
                                                    }}>
                                                        {option.displaySlug}
                                                    </span>
                                                    <span style={{
                                                        color: '#808080',
                                                        fontFamily: 'Roboto',
                                                        fontSize: '16px',
                                                        fontStyle: 'normal',
                                                        fontWeight: '400',
                                                        lineHeight: '150%'
                                                    }}>
                                                        {option.label}
                                                    </span>
                                                </div>
                                                {field.value === option.value && (
                                                    <Check className="h-4 w-4 text-[#191919] ml-2" />
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {meta?.touched && meta?.error && form?.submitCount > 0 && (
                        <div style={{ color: '#CB3434', fontSize: '14px' }}>
                            {meta?.error}
                        </div>
                    )}
                </div>
            )}
        </Field>
    )
}

export default SearchableSelect